import {Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {map, switchMap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {ToastrService} from 'ngx-toastr';

import {Product} from '../../../../../models/product.model';
import {ModalService} from '../../../../../services/modal.service';
import {ChiliGrafixService} from '../../../../../services/chiligrafix.service';
import {ChiliProjectData, ChiliSelectResult} from '../../../../../interfaces/chili-grafix';
import {OrderItem} from '../../../../../models/order-item.model';
import {Order} from '../../../../../models/order.model';
import {OrderItemDelivery} from '../../../../../models/order-item-delivery.model';
import {NgxGalleryOptions, NgxGalleryImage, NgxGalleryComponent} from '../../../../../ngx-gallery';

@Component({
    selector: 'app-customization-control',
    templateUrl: './customization-control.component.html',
    styleUrls: ['./customization-control.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class CustomizationControlComponent {

  isFetching = false;
  isFetchingViewCustomization = false;
  @Input() orderItem: OrderItem;
  private _orderItemDelivery: OrderItemDelivery;
  @Input() order: Order;
  @Input() readonly  = false;
  @Input() view: 'grid' | 'item' = 'item';

  @Output() onUpdate: EventEmitter<OrderItemDelivery> = new EventEmitter();

  @ViewChild('customizationGallery', { static: false }) gallery: NgxGalleryComponent;

  galleryOptions: NgxGalleryOptions[];
  galleryImages: NgxGalleryImage[];

  @Input()
  set orderItemDelivery(value: OrderItemDelivery) {
    this._orderItemDelivery = value;

    // Set up gallery images when orderItemDelivery is set
    if (value && value.chiliOutput) {
      this.galleryImages = [{
        small: value.chiliOutput,
        medium: value.chiliOutput,
        big: value.chiliOutput,
        type: 'image'
      }];
    }
  }

  get orderItemDelivery(): OrderItemDelivery {
    return this._orderItemDelivery;
  }

  constructor(private modalService: ModalService, private chiliService: ChiliGrafixService, private toastr: ToastrService) {
    this.initializeGallery();
  }

  private initializeGallery(): void {
    this.galleryOptions = [{
      preview: true,
      previewFullscreen: false,
      previewForceFullscreen: true,
      previewCloseOnClick: true,
      previewCloseOnEsc: true,
      previewKeyboardNavigation: false,
      previewArrows: false,
      previewZoom: false,
      previewRotate: false,
      previewDownload: false,
      image: false,
      thumbnails: false,
      width: '0px',
      height: '0px'
    }];

    // Initialize images array to empty
    this.galleryImages = [];
  }

  get hasCustomizations(): boolean {
    return this.product && this.product.isCustomizable;
  }

  handleViewCustomization(): void {
    if (!this.orderItem || !this.product || !this.order || !this.orderItemDelivery.chiliOutput) {
      this.toastr.error('Some input data are missing. Please contact the support');
      return;
    }

    // Gallery images are already set up in the orderItemDelivery setter
    // Just open the gallery in preview mode (full-screen)
    setTimeout(() => {
      if (this.gallery) {
        this.gallery.openPreview(0);
      } else {
        this.toastr.error('Unable to open preview. Please try again.');
      }
    }, 100);
  }

  //  when output is not  available user can still  see  customization  in readonly mode
  showChiliStudioInReadonlyMode(): void {
    this.callCustomizationDialog(true);
  }

  handleCustomizationClick(): void {

    if (this.isFetching) {
      return; // Prevent multiple clicks
    }

    if (!this.orderItem || !this.product || !this.order) {
      this.toastr.error('Some input data are missing. Please contact the support');
      return;
    }

    this.isFetching = true;
    this.callCustomizationDialog();
  }

  get product(): Product {
    return this.orderItem?.product;
  }

  private getChiliProjectId(): Observable<string> {
    if (this.orderItemDelivery.chiliProjectId) {
      return of(this.orderItemDelivery.chiliProjectId);
    }

    return this.chiliService.getChiliProjectIdForProductId(this.product.id);
  }

  get alreadyCustomized(): boolean {
    return this.hasCustomizations && this.orderItemDelivery?.isCustomized === true;
  }

  get canCustomize() {
    return  this.orderItem.canCustomize
  }


  private callCustomizationDialog(readonly = false) {
    this.chiliService.getChiliConfig().pipe(
      switchMap( config => {
        if (!config) {
          return of(null)
        }

        return this.getChiliProjectId().pipe(
          map( projectId => {
            if (projectId) {
              config.chiliProjectId = projectId;
              return config;
            }
          })
        );
      }),
      map(config => {
        this.isFetchingViewCustomization = false;
        this.isFetching = false;
        if (!config || !config.authToken) {
          this.toastr.error('Failed to fetch the configuration for customization. Please try again later.');
          return null;
        }

        const chiliData: ChiliProjectData = {
          chiliConfig: config,
          order: this.order,
          orderItem: this.orderItem,
          delivery: this.orderItemDelivery
        }
        return chiliData;

      }),
      switchMap( chiliData => {
        if (!chiliData) {
          return of(null);
        }
        return this.callSelectCustomizationDialog(chiliData);
      }),
      switchMap ( ({chiliData, customizedDelivery}: {chiliData: ChiliProjectData | null, customizedDelivery: OrderItemDelivery | null}) => {
        if (!chiliData) {
          return of(null);
        }

        if (customizedDelivery) {
          return of(customizedDelivery);
        }
        return this.modalService.showProductCustomizationModal(chiliData, readonly);
      }),
    ).subscribe( result => {
      if (result) {
        this.orderItemDelivery = result;
        this.orderItem.updateDelivery(result);
        this.onUpdate.emit(result);
      }
      this.isFetchingViewCustomization = false;
      this.isFetching = false;

    });
  }

  // it should  check  if user can select an existing customization or create a new one
  private  callSelectCustomizationDialog(chiliData: ChiliProjectData):
    Observable<{chiliData: ChiliProjectData | null, customizedDelivery: OrderItemDelivery | null}> {
    if (!chiliData.orderItem || !chiliData.orderItem.isCustomized || chiliData.delivery.isCustomized) {
      return of({chiliData, customizedDelivery: null});
    }

    // showCustomizationSelection returns ChiliSelectResult (existing doc), 'new' (new customization), or undefined (cancelled)
    return this.modalService.showCustomizationSelection(chiliData.orderItem).pipe( switchMap((chiliDoc: string | ChiliSelectResult | undefined) => {
      if (!chiliDoc) {
        // cancel  whole process
        return of({chiliData: null, customizedDelivery: null});
      }

      if (chiliDoc === 'new') {
        return of({chiliData, customizedDelivery: null});
      }

      if (typeof chiliDoc === 'string') {
        // any other plain string (not 'new') — treat as new customization
        return of({chiliData, customizedDelivery: null});
      }

      const customizedDelivery = new OrderItemDelivery(chiliData.delivery);
      customizedDelivery.chili_project_id = chiliData.chiliConfig.chiliProjectId;
      customizedDelivery.chili_data = chiliDoc.chiliDoc;
      customizedDelivery.chili_design_name = chiliDoc?.title || '';
      customizedDelivery.chili_output = chiliDoc.chiliOutput;
      customizedDelivery.chili_output_thumbnail = chiliDoc.chiliOutputPreview;

      if (customizedDelivery.chili_output && customizedDelivery.chili_output_thumbnail) {
        return of({chiliData, customizedDelivery});
      }

      return this.chiliService.createChiliPreview(
        customizedDelivery.chili_project_id,
        customizedDelivery.chili_data,
      ).pipe(map(preview => {
        if (preview) {
          customizedDelivery.chili_output = preview.chili_output;
          customizedDelivery.chili_output_thumbnail = preview.chili_output_thumbnail;
        }

      return {chiliData, customizedDelivery};
      }));
    }));

  }

  get hasOutput(): boolean {
    return !!this.orderItemDelivery.chiliOutput;
  }


  get isOutputImage(): boolean {
    return this.orderItemDelivery.isChiliOutputImage;
  }

  get hasThumbnail(): boolean {
    return !!this.orderItemDelivery.chiliOutputThumbnail;
  }
}
